import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getSessionId() {
  let sid = sessionStorage.getItem('gl_session_id');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('gl_session_id', sid);
  }
  return sid;
}

export default function usePageTracking() {
  const location = useLocation();
  const { user } = useAuth();
  const lastPage = useRef(null);
  const lastTime = useRef(0);
  const pageStartTime = useRef(Date.now());
  const heartbeatRef = useRef(null);

  // Send page duration for the previous page
  const sendPageDuration = useCallback((page, duration_ms) => {
    if (!page || duration_ms <= 0 || duration_ms > 1800000) return; // cap at 30 min
    api.post('/analytics/pageview', {
      page,
      user_id: user?.user_id,
      session_id: getSessionId(),
      duration_ms,
      event_type: 'page_duration'
    }).catch(() => {});
  }, [user?.user_id]);

  // Track page views and dwell time
  useEffect(() => {
    const now = Date.now();
    if (location.pathname === lastPage.current && now - lastTime.current < 2000) {
      return;
    }

    // Send duration for the previous page
    if (lastPage.current) {
      const duration = now - pageStartTime.current;
      sendPageDuration(lastPage.current, duration);
    }

    // Record new page
    lastPage.current = location.pathname;
    lastTime.current = now;
    pageStartTime.current = now;

    api.post('/analytics/pageview', {
      page: location.pathname,
      user_id: user?.user_id,
      session_id: getSessionId(),
      event_type: 'pageview'
    }).catch(() => {});
  }, [location.pathname, user?.user_id, sendPageDuration]);

  // Heartbeat every 60 seconds
  useEffect(() => {
    heartbeatRef.current = setInterval(() => {
      api.post('/analytics/pageview', {
        event_type: 'heartbeat',
        session_id: getSessionId(),
        user_id: user?.user_id
      }).catch(() => {});
    }, 60000);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [user?.user_id]);

  // Send final page duration on beforeunload via sendBeacon
  useEffect(() => {
    const handleBeforeUnload = () => {
      const duration = Date.now() - pageStartTime.current;
      if (lastPage.current && duration > 0 && duration < 1800000) {
        const payload = JSON.stringify({
          page: lastPage.current,
          user_id: user?.user_id,
          session_id: getSessionId(),
          duration_ms: duration,
          event_type: 'page_duration'
        });
        navigator.sendBeacon(`${API_URL}/api/analytics/pageview`, payload);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user?.user_id]);
}
